/**
 * 広告画像エディタ用の undo/redo 履歴フック (Phase 32.1)
 *
 * 設計:
 * - スライド全体（AdImageSlide[]）をスナップショットとして保持
 * - commit(next): 新しいスナップショットを past に push、future をクリア
 * - undo / redo: past <-> future 間でスナップショットを移動
 * - エディタの「初期同期」（adImage 取得直後）はリセットして履歴を空にする
 *
 * 過剰な commit を避けるため、コンポーネント側で連続変更を debounce することを推奨。
 * 現状の AdImageEditorPage では文字入力等もリアルタイムに commit される点に注意。
 */
import { useCallback, useRef, useState } from 'react';

export interface UseHistoryResult<T> {
  /** 現在の値（外部 setState の代わりにこれを参照） */
  state: T;
  /** 履歴に commit して新しい値に切り替える */
  commit: (next: T | ((prev: T) => T)) => void;
  /** 履歴に commit せずに同期するためのリセット（外部データ取得直後など） */
  reset: (next: T) => void;
  /**
   * Phase 33.1: ドラッグ等の連続操作中、commit を「past に積まない」モードに切替。
   * - pauseHistory(): 現在の状態をスナップショットとして保持。以降の commit は state は更新するが past には積まない
   * - resumeHistory(): pause 中の最終状態と pause 直前のスナップショットを比較し、差分があれば「ひとまとめの 1 ステップ」として past に push
   */
  pauseHistory: () => void;
  resumeHistory: () => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
}

const MAX_HISTORY = 50;

export function useHistory<T>(initial: T): UseHistoryResult<T> {
  const [state, setState] = useState<T>(initial);
  const pastRef = useRef<T[]>([]);
  const futureRef = useRef<T[]>([]);
  // pause 中のスナップショット（pauseHistory 呼び出し時点の state）
  const pausedSnapshotRef = useRef<{ snapshot: T } | null>(null);
  const [, forceRender] = useState(0);

  const commit = useCallback(
    (next: T | ((prev: T) => T)) => {
      setState((prev) => {
        const value = typeof next === 'function' ? (next as (p: T) => T)(prev) : next;
        // 同値ガード: 参照同一なら履歴に積まない
        if (Object.is(value, prev)) return prev;
        // Pause 中は past に積まず、state だけ更新
        if (pausedSnapshotRef.current !== null) {
          forceRender((n) => n + 1);
          return value;
        }
        pastRef.current.push(prev);
        if (pastRef.current.length > MAX_HISTORY) {
          pastRef.current.shift();
        }
        futureRef.current = [];
        forceRender((n) => n + 1);
        return value;
      });
    },
    [],
  );

  const reset = useCallback((next: T) => {
    pastRef.current = [];
    futureRef.current = [];
    pausedSnapshotRef.current = null;
    setState(next);
    forceRender((n) => n + 1);
  }, []);

  const pauseHistory = useCallback(() => {
    // 多重 pause は無視（最初のスナップショットを保持）
    if (pausedSnapshotRef.current !== null) return;
    setState((prev) => {
      pausedSnapshotRef.current = { snapshot: prev };
      return prev;
    });
  }, []);

  const resumeHistory = useCallback(() => {
    const paused = pausedSnapshotRef.current;
    pausedSnapshotRef.current = null;
    if (!paused) return;
    setState((prev) => {
      if (!Object.is(prev, paused.snapshot)) {
        pastRef.current.push(paused.snapshot);
        if (pastRef.current.length > MAX_HISTORY) {
          pastRef.current.shift();
        }
        futureRef.current = [];
        forceRender((n) => n + 1);
      }
      return prev;
    });
  }, []);

  const undo = useCallback(() => {
    setState((prev) => {
      const past = pastRef.current.pop();
      if (past === undefined) return prev;
      futureRef.current.push(prev);
      forceRender((n) => n + 1);
      return past;
    });
  }, []);

  const redo = useCallback(() => {
    setState((prev) => {
      const next = futureRef.current.pop();
      if (next === undefined) return prev;
      pastRef.current.push(prev);
      forceRender((n) => n + 1);
      return next;
    });
  }, []);

  return {
    state,
    commit,
    reset,
    pauseHistory,
    resumeHistory,
    undo,
    redo,
    canUndo: pastRef.current.length > 0,
    canRedo: futureRef.current.length > 0,
  };
}
